// Boss fights on the board: a boss duel's blow log (shared/boss-duel.ts) turned into a board result, so the Bosses
// page, the Boss Ladder and the World Boss play their fights on the same stage as PvP (client/boardview.ts), with the
// boss alone on its half, drawn big, in its own lair (client/arenascene.ts lairGround). The duel decides; this only acts it out. Results saved before
// blows carried their time (no `ms`) have no replay.
import type { BattleResult, Blow } from '../shared/arena.ts';
import type { BossResult, CreatureDTO } from '../shared/protocol.ts';
import { chartMult, type Fighter } from '../shared/combat.ts';
import { MOVE } from '../shared/moves.ts';
import { SPECIES } from '../shared/species.ts';
import { S } from './state.ts';
import { boardView, boardChips, type StageOpts } from './boardview.ts';
import { mySprite } from './trainerart.ts';

export interface BossFight {
  boss: string;
  /** The boss's level, and the level the team fought at (the World Boss's), when not their own. */
  level: number;
  teamLevel?: number;
  win: boolean;
  log: BossResult['log'];
  bossMaxHp: number;
  teamMaxHp: Record<number, number>;
  /** The team in the order it fought. */
  team: { id: number; species: string; level: number; aura?: boolean }[];
}

/** The team of a Bosses page or Ladder result, from the creatures you own (ids in fighting order). */
export function teamOf(b: Pick<BossResult, 'ids' | 'teamMaxHp'>): BossFight['team'] {
  const ids = b.ids ?? Object.keys(b.teamMaxHp).map(Number);
  return ids.map((id) => S.creatures.get(id)).filter((c): c is CreatureDTO => !!c).map((c) => ({ id: c.id, species: c.species, level: c.level, aura: c.rolls.aura }));
}

const made = new WeakMap<object, BattleResult | null>();

/** The board result already made for `key` (bossBoardHtml), or null. */
export const bossBattleOf = (key: object): BattleResult | null => made.get(key) ?? null;

/** The fight as a board result, or null when it can't be replayed. Kept per result so its replay clock holds. */
export function bossBattle(f: BossFight, key: object = f): BattleResult | null {
  if (made.has(key)) return made.get(key)!;
  const r = build(f);
  made.set(key, r);
  return r;
}

function build(f: BossFight): BattleResult | null {
  if (!f.log.length || f.log.some((x) => x.ms === undefined)) return null;
  const team = f.team.filter((c) => f.teamMaxHp[c.id] !== undefined);
  const at = new Map(team.map((c, i) => [c.id, i]));
  if (f.log.some((x) => !at.has(x.id))) return null;
  const bossDef = { speciesId: f.boss } as Fighter;
  const log: Blow[] = f.log.map((x) => {
    const i = at.get(x.id)!;
    const mv = x.mv ? MOVE[x.mv] : undefined;
    const mult = mv ? chartMult(mv, x.by === 'team' ? bossDef : ({ speciesId: team[i].species } as Fighter), 'base') : 1;
    return x.by === 'team'
      ? { ms: x.ms!, s: 0, a: i, d: 0, dmg: x.dmg, crit: x.crit, mult, hp: x.hp, mv: x.mv }
      : { ms: x.ms!, s: 1, a: 0, d: i, dmg: x.dmg, crit: x.crit, mult, hp: x.hp, mv: x.mv };
  });
  const lvl = (c: { level: number }) => f.teamLevel ?? c.level;
  return {
    boss: f.boss,
    sides: [
      { name: 'You', team: team.map((c) => ({ id: c.id, species: c.species, level: c.level, arenaLevel: lvl(c), maxHp: f.teamMaxHp[c.id], aura: !!c.aura, row: 'front' as const })) },
      { name: SPECIES[f.boss]?.name ?? f.boss, team: [{ id: -1, species: f.boss, level: f.level, arenaLevel: f.level, maxHp: f.bossMaxHp, aura: false, row: 'front' as const }] },
    ],
    log, winner: f.win ? 0 : 1, ms: log.length ? log[log.length - 1].ms : 0, timedOut: false,
  };
}

/** The replay on the board, or '' when there is none. `opts`: anything beyond the lair and your trainer. */
export function bossBoardHtml(f: BossFight, key: object, opts: StageOpts = {}): string {
  const r = bossBattle(f, key);
  if (!r) return '';
  const name = SPECIES[f.boss]?.name ?? f.boss;
  return `<div class="tl-board boss-board">${boardView(r, 0, ['You', name], 'plain', { scene: `lair-${f.boss}`, trainers: [mySprite(), null], ...opts })}${boardChips(r, 0, ['You', name])}</div>`;
}
